import React, { useState } from "react";
import { Box, Typography, Paper, Button, Stack, Chip } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import { getTicketDetails } from "app/services/schoolReportingServices/zohoDesk.service";
import { useQuery } from "react-query";
import CloseIcon from "@mui/icons-material/Close";
import TicketReplyPreview from "./TicketConversationPreview";

const TicketDetails = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);

  // Fetch ticket details using React Query
  const {
    data: ticketResponse,
    isLoading,
    error,
  } = useQuery(["ticket", ticketId], () => getTicketDetails(ticketId), {
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || "Failed to fetch ticket details"
      );
    },
  });

  const ticket = ticketResponse?.data?.ticket;
  const conversations = ticketResponse?.data?.conversations || [];
  if (isLoading) {
    return (
      <img
        src={previewImg}
        alt="Preview"
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          width: "auto",
          height: "auto",
          objectFit: "contain",
          borderRadius: 8,
          display: "block",
        }}
      />
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Failed to load ticket details</Typography>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Back to Tickets
        </Button>
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">No ticket data found</Typography>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Back to Tickets
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5">Ticket #{ticket.ticketId}</Typography>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back to Tickets
        </Button>
      </Stack>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack spacing={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">{ticket.subject}</Typography>
            {/* Status color coding as per index.jsx */}
            {(() => {
              const value = ticket.status;
              let color = "black";
              if (value === "Closed") color = "green";
              else if (value === "Excalated") color = "#001F54"; // Darkest Blue
              else if (value === "On Hold") color = "orange";
              else if (value === "Open") color = "black";
              return (
                <Chip
                  label={value}
                  sx={{
                    bgcolor: color,
                    color: color === "black" ? "#fff" : "#fff",
                    fontWeight: 600,
                  }}
                />
              );
            })()}
          </Stack>
          <Stack direction="row" spacing={2}>
            <Typography color="text.secondary">Priority:</Typography>
            <Typography
              sx={{
                color:
                  ticket.priority === "High"
                    ? "error.main"
                    : ticket.priority === "Medium"
                    ? "warning.main"
                    : "success.main",
              }}
            >
              {ticket.priority}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Typography color="text.secondary">Module:</Typography>
            <Typography>{ticket.module}</Typography>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Typography color="text.secondary">Screen:</Typography>
            <Typography>{ticket.screenName}</Typography>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Typography color="text.secondary">Created By:</Typography>
            <Typography>{ticket.createdBy?.personName}</Typography>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Typography color="text.secondary">Created On:</Typography>
            <Typography>
              {moment(ticket.createdOn).format("DD MMM, YYYY HH:mm")}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Typography color="text.secondary">Description :</Typography>
            <Typography>{ticket.description}</Typography>
          </Stack>
        </Stack>
      </Paper>

      <Typography variant="h6" mb={2}>
        Attachments
      </Typography>

      {ticket.attachments && ticket.attachments.length > 0 ? (
        <Stack direction="row" flexWrap="wrap" gap={2} mb={3}>
          {ticket.attachments.map((attachment, index) => {
            const isPdf =
              typeof attachment === "string" &&
              attachment.toLowerCase().endsWith(".pdf");
            return (
              <Box
                key={index}
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 2,
                  overflow: "hidden",
                  boxShadow: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  background: "#f5f5f5",
                  position: "relative",
                }}
                onClick={() => {
                  setPreviewImg(attachment);
                  setPreviewOpen(true);
                }}
              >
                {isPdf ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="red"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      PDF
                    </Typography>
                  </Box>
                ) : (
                  <img
                    src={attachment}
                    alt={attachment.filename}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Typography color="text.secondary" mb={3}>
          No attachments
        </Typography>
      )}

      {/* Attachment Preview Modal (Image or PDF) */}
      {previewOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(0,0,0,0.7)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              position: "relative",
              bgcolor: "background.paper",
              borderRadius: 2,
              boxShadow: 24,
              p: 2,
              width: "70vw",
              height: "70vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              overflow: "hidden",
            }}
          >
            <CloseIcon
              sx={{ cursor: "pointer", mb: 1 }}
              onClick={() => setPreviewOpen(false)}
            />
            {typeof previewImg === "string" &&
            previewImg.toLowerCase().endsWith(".pdf") ? (
              <Box
                sx={{
                  width: "66vw",
                  height: "66vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <iframe
                  src={previewImg}
                  title="PDF Preview"
                  style={{ width: "100%", height: "100%", border: 0 }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: "66vw",
                  height: "66vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <img
                  src={previewImg}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    borderRadius: 8,
                    display: "block",
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Conversation History */}
      <Typography variant="h6" mb={2}>
        EkalSutra Replies
      </Typography>
      <TicketReplyPreview
        conversations={conversations}
        ticketId={ticket.ticketId}
      />
    </Box>
  );
};

export default TicketDetails;
